// components/Students/EditStudentPage.js
import React, { useEffect, useState } from "react";
import StudentForm from "./StudentForm";
import apiClient from "../../utils/apiClient";
import { useNavigate, useParams } from "react-router-dom";
import { Spinner, Alert } from "react-bootstrap";

const initialForm = {
  name: "",
  email: "",
  mobile: "",
  dob: "",
  gender: "",
  address: "",
  course: "",
  batch: "",
  degree: "",
  passout: "",
  experience: "",
  totalFees: "",
  paidFees: "",
  pendingFees: "",
  placement: "Yes",
  refName: "",
  refNumber: "",
  comments: "",
};

const EditStudentPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [form, setForm] = useState(initialForm);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  const [saving, setSaving] = useState(false);
  const [courseOptions, setCourseOptions] = useState([]);
  const [batchOptions, setBatchOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [initialAdditionalCourses, setInitialAdditionalCourses] = useState([]);

  // Fetch student info and courses
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setErr("");
      try {
        const [{ data: sData }, { data: cData }] = await Promise.all([
          apiClient.get(`/students/${id}`),
          apiClient.get("/courses/list"),
        ]);
        const student = sData.student;
        setForm({
          ...initialForm,
          ...student,
          course: student.course?._id || "",
          batch: student.batch?._id || "",
        });
        setCourseOptions((cData.courses || []).map(c => ({ value: c._id, label: c.name, fees: c.fees || c.price || '' })));

        // Load additional courses from courseAssignments (non-primary)
        const assignments = sData.courseAssignments || [];
        const additional = assignments
          .filter(a => !a.isPrimary)
          .map(a => ({
            courseId: a.courseId?._id || a.courseId || '',
            batchId: a.batchId?._id || a.batchId || '',
            totalFees: a.totalFees || '',
          }));
        setInitialAdditionalCourses(additional);
      } catch {
        setErr("Failed to load student details.");
      }
      setLoading(false);
    };
    fetchData();
  }, [id]);

  // Fetch batches for selected course
  const fetchBatches = async (courseId) => {
    if (!courseId) return setBatchOptions([]);
    try {
      const { data } = await apiClient.get("/batches/list", { params: { course: courseId } });
      setBatchOptions((data.batches || []).map(b => ({ value: b._id, label: b.name })));
    } catch {
      setBatchOptions([]);
    }
  };

  // Refetch batches when course is loaded or changes
  useEffect(() => {
    if (form.course) fetchBatches(form.course);
  }, [form.course]);

  const handleEdit = async (formData, additionalCourses = []) => {
    setSaving(true);
    try {
      const payload = { ...formData };
      if (!payload.course) payload.course = null;
      if (!payload.batch) payload.batch = null;
      // Include additional courses in payload
      payload.additionalCourses = additionalCourses.filter(ac => ac.courseId);
      await apiClient.put(`/students/${id}`, payload);
      setToast({ show: true, message: "Student updated successfully!", type: "success" });
      setTimeout(() => navigate(`/students/${id}`), 1200);
    } catch {
      setToast({ show: true, message: "Failed to update student.", type: "error" });
    }
    setSaving(false);
  };

  if (loading)
    return <div className="text-center my-5"><Spinner animation="border" /></div>;
  if (err)
    return <Alert variant="danger" className="my-5 text-center">{err}</Alert>;

  return (
    <StudentForm
      initialData={form}
      courseOptions={courseOptions}
      batchOptions={batchOptions}
      loading={saving}
      toast={toast}
      setToast={setToast}
      mode="edit"
      onSubmit={handleEdit}
      onCancel={() => navigate(-1)}
      onChangeCourse={fetchBatches}
      initialAdditionalCourses={initialAdditionalCourses}
    />
  );
};

export default EditStudentPage;
